import { FormGroup, Progress, Radio, RadioItem } from '@agro-club/agroclub-shared'
import { FormikContextType } from 'formik'
import { CompanyAddDTO, CompanyEditDTO, Prepayment } from 'modules/domain/company/types'

import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { FormSelectDadata } from 'views/components/FormSelectDadata/FormSelectDadata'
import { SharedModalContent, SharedModalInputs, SharedModalTitle } from 'views/components/SharedModal/styled'
import { NumberInput } from 'views/ui/NumberInput/NumberInput'
import { FormItemWrapper } from 'views/styled/common'
import ASharedModal from 'views/components/SharedModal/ASharedModal'
import { AnalyticsPlaces } from '../../analyticsPlaces'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import { FC, ReactNode } from 'react'
import { CompanySize } from 'modules/domain/user/types'
import { FormInput } from 'views/components/form/FormInput'
import FormSelectShared from 'views/components/FormSelectShared'
import { endpoints } from 'modules/endpoints'

type Props = {
  formik: FormikContextType<CompanyEditDTO> | FormikContextType<CompanyAddDTO>
  progress: Progress
  onClose: () => void
  isAdd?: boolean
  children?: ReactNode
}

const isAddForm = (
  formik: FormikContextType<CompanyEditDTO> | FormikContextType<CompanyAddDTO>,
): formik is FormikContextType<CompanyAddDTO> => 'company_size' in formik.values

export const ShortNumberWrapper = styled.div`
  max-width: 94px;
`

export const CompanyRuDetailsModal: FC<Props> = ({ formik, progress, onClose, isAdd, children }) => {
  const { t } = useTranslation('company')

  return (
    <ASharedModal id={AnalyticsPlaces.DETAILS_MODAL} size="medium" onClose={onClose}>
      <SharedModalTitle>{t('commonInfo')}</SharedModalTitle>
      <SharedModalContent>
        <SharedModalInputs>
          {isAdd && isAddForm(formik) && (
            <FormSelectDadata
              error={formik.errors.name || formik.errors.inn}
              onChange={(c) => {
                formik.setValues({ ...formik.values, name: c?.name_short ?? c?.name_full, inn: c?.inn ?? null })
              }}
              required
            />
          )}
          {children}
          <FormGroup label={t('form.fields.vat')} error={formik.errors.vat}>
            <FormItemWrapper>
              <Radio
                value={formik.values.vat}
                onChange={(val) => {
                  formik.setFieldValue('vat', val)
                }}
              >
                <RadioItem value={true} label={t('common:thereIs')} />
                <RadioItem value={false} label={t('common:no')} />
              </Radio>
            </FormItemWrapper>
          </FormGroup>
          <FormGroup label={t('form.fields.prepayment')} error={formik.errors.prepayment}>
            <FormItemWrapper>
              <Radio
                value={formik.values.prepayment}
                onChange={(val) => {
                  formik.setFieldValue('prepayment', val)
                }}
              >
                <RadioItem value={Prepayment.yes} label={t('form.prepaymentValues.yes')} />
                <RadioItem value={Prepayment.no} label={t('form.prepaymentValues.no')} />
                <RadioItem value={Prepayment.unknown} label={t('form.prepaymentValues.unknown')} />
              </Radio>
            </FormItemWrapper>
          </FormGroup>
          <FormGroup label={t('form.fields.payment_delay_days')} error={formik.errors.payment_delay_days}>
            <ShortNumberWrapper>
              <NumberInput
                isInteger
                onChange={(value) => formik.setFieldValue('payment_delay_days', value || 0)}
                value={formik.values.payment_delay_days || 0}
              />
            </ShortNumberWrapper>
          </FormGroup>
          <FormGroup label={t('typeCompany')} error={formik.errors.company_size}>
            <FormItemWrapper>
              <Radio
                value={formik.values.company_size}
                onChange={(val) => {
                  formik.setFieldValue('company_size', val)
                }}
              >
                <RadioItem value={CompanySize.big_company} label={t('large')} />
                <RadioItem value={CompanySize.small_company} label={t('small')} />
                <RadioItem value={null} label={t('notSpecified')} />
              </Radio>
            </FormItemWrapper>
          </FormGroup>
          <FormSelectShared
            label={t('user:mainCompany')}
            endpoint={`${endpoints.company()}?parents_only=True`}
            onChange={() => {
              formik.setFieldValue('companies_group_name', '')
            }}
            formik={formik}
            fieldName="parent"
            placeholder=""
            searchField="search"
          />
          <FormInput
            label={t('Название группы компаний')}
            field="companies_group_name"
            formik={formik}
            disabled={!!formik.values.parent}
          />
        </SharedModalInputs>
      </SharedModalContent>

      <ADefaultModalFooter
        onClose={onClose}
        onConfirm={formik.handleSubmit}
        progress={progress}
        confirmButtonText={t('common:save')}
      />
    </ASharedModal>
  )
}
